<script setup lang="ts">
import Icon from '@/components/Icon.vue';
defineProps<{ menuId: string; arrowClass?: string }>();
</script>

<template>
    <div class="cb-panel" role="dialog" :id="menuId">
        <!-- caret -->
        <span class="cb-caret" :class="arrowClass" />

        <!-- header -->
        <div class="cb-head flex items-center justify-between">
            <p class="cb-head-title">Contact us</p>
            <button
                class="close-button text-xl mt-2 text-slate-400 border-none hover:text-slate-600 focus:outline-none"
                aria-label="Close menu"
                @click="$emit('close')"
            >
                <Icon name="no" />
            </button>
        </div>

        <!-- list -->
        <ul class="cb-list divide-y divide-slate-200">
            <slot />
        </ul>
    </div>
</template>

<style scoped>
.close-button {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: color 0.15s ease;
    background-color: transparent;
}
.cb-panel {
    position: absolute; /* parent supplies exact anchor via :class */
    width: 20rem; /* w-80 */
    border: 1px solid #d5dbe7; /* darker than before for definition */
    border-radius: 14px;
    background: #ffffff; /* solid white so elements don't blend */
    backdrop-filter: blur(4px);
    overflow: hidden;

    /* subtle blue-ish halo to separate from page whites */
    box-shadow:
        0 0 0 1px rgba(37, 99, 235, 0.08),
        0 18px 40px -18px rgba(15, 23, 42, 0.25),
        0 6px 16px -8px rgba(15, 23, 42, 0.15);
    backdrop-filter: blur(4px);
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    will-change: opacity, transform;
}

/* caret: a tad larger, with a white halo and stronger outline */
.cb-caret {
    position: absolute;
    display: block;
    width: 14px;
    height: 14px;
    transform: rotate(45deg);
    background: #ffffff;
    border: 1px solid #cbd3e2; /* slightly darker edge */
    box-shadow:
        0 0 0 2px #ffffff,
        /* white halo so it doesn't blend */ 0 4px 10px rgba(15, 23, 42, 0.15);
}

@media (max-width: 640px) {
    .cb-panel {
        backdrop-filter: none;
    }
    /* caret shadow slightly simpler to match non-blurred panel */
    .cb-caret {
        box-shadow:
            0 0 0 2px #ffffff,
            0 2px 6px rgba(15, 23, 42, 0.12);
    }

    .cb-panel,
    .cb-panel * {
        transform: none !important;
        backface-visibility: hidden; /* prevent raster swaps during scroll */
    }
}

/* header with rounded top blue accent bar */
.cb-head {
    position: relative;
    padding: 0.55rem 0.85rem;
    background: #fff;
    border-bottom: 1px solid #e2e7f1;
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
    overflow: hidden; /* keeps accent rounded at corners */
}

/* blue accent bar at the top */
.cb-head::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px; /* bar thickness */
    background: linear-gradient(90deg, #2563eb, #3b82f6);
    border-top-left-radius: 14px;
    border-top-right-radius: 14px;
}
.cb-head-title {
    margin: 0.5rem 0 0 0;
    font-weight: 600;
    font-size: 0.92rem;
    color: #111827;
    letter-spacing: 0.2px;
}

/* list + dividers that actually read */
.cb-list {
    list-style: none;
    padding: 0.25rem;
    margin: 0;
}
</style>
